import { bedrockSeiten, blaetterSlots, javaAnsicht, slotAnzahl, type Daten, type Form, type Katalog, type UiDaten } from "@gui-creator/gemeinsam";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { BedrockFormular } from "../bedrock/BedrockFormular.tsx";
import { Formwahl } from "../inventar/Formwahl.tsx";
import { Inventar, type Fremde, type SlotMarke } from "../inventar/Inventar.tsx";
import { skalierung } from "../inventar/masse.ts";
import "./zeichenflaeche.css";

type Ansicht = "beide" | "java" | "bedrock";

const FORM_TEXT = (f: Form) => (f.art === "truhe" ? (f.reihen === 6 ? "Doppeltruhe" : `Truhe · ${f.reihen} ${f.reihen === 1 ? "Reihe" : "Reihen"}`) : f.art === "trichter" ? "Trichter" : "Spender");

type Props = {
  daten: UiDaten;
  katalog: Katalog;
  quellen: Daten;
  zustaende: string[];
  zustand: string | null;
  onZustand: (z: string | null) => void;
  verweise: (id: string) => string | undefined;
  darfBearbeiten: boolean;
  onForm: (f: Form) => void;
  auswahl?: ReadonlySet<number>;
  fokus?: number | null;
  notizen?: ReadonlySet<number>;
  fremde?: Fremde[];
  onSlot?: (index: number, e: MouseEvent | KeyboardEvent) => void;
  onTaste?: (e: KeyboardEvent) => void;
  slotHuelle?: (index: number, inhalt: ReactNode) => ReactNode;
  werkzeuge?: ReactNode;
};

export function Zeichenflaeche(p: Props) {
  const buehne = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState(1000);
  const [seite, setSeite] = useState(0);
  const [ansicht, setAnsicht] = useState<Ansicht>("beide");

  useLayoutEffect(() => {
    const el = buehne.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBreite(e!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const nebeneinander = breite >= 176 * 2 + 320 + 96;
  const sichtbar: Ansicht = nebeneinander ? "beide" : ansicht === "beide" ? "java" : ansicht;
  const skala = skalierung(nebeneinander ? breite - 320 - 96 : breite - 32, 9);

  const java = useMemo(() => javaAnsicht(p.daten, p.katalog, p.quellen, seite, p.verweise), [p.daten, p.katalog, p.quellen, seite, p.verweise]);
  const bedrock = useMemo(() => bedrockSeiten(p.daten, p.katalog, p.quellen, p.verweise), [p.daten, p.katalog, p.quellen, p.verweise]);

  const listen = useMemo(() => new Set(p.daten.listen.flatMap((l) => l.slots)), [p.daten.listen]);
  const blaettern = useMemo(() => new Set(blaetterSlots(p.daten)), [p.daten]);
  const markierung = (i: number): SlotMarke => (listen.has(i) ? "liste" : blaettern.has(i) ? "blaettern" : null);

  return (
    <section className="zeichenflaeche" aria-label="Zeichenfläche">
      <div className="werkzeugleiste" role="toolbar" aria-label="Ansicht und Form">
        <Formwahl ui={p.daten} deaktiviert={!p.darfBearbeiten} onWaehlen={p.onForm} />
        {java.seiten > 1 && (
          <div className="seitenwahl" aria-label="Java-Seite">
            <button type="button" className="knopf knopf-still knopf-symbol" aria-label="Vorige Seite" disabled={java.seite === 0} onClick={() => setSeite(java.seite - 1)}>
              <ChevronLeft size={14} aria-hidden="true" />
            </button>
            <span className="zahlen">
              Seite {java.seite + 1} / {java.seiten}
            </span>
            <button type="button" className="knopf knopf-still knopf-symbol" aria-label="Nächste Seite" disabled={java.seite >= java.seiten - 1} onClick={() => setSeite(java.seite + 1)}>
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        )}
        {p.zustaende.length > 0 && (
          <label className="werkzeug-feld werkzeug-zustand">
            <span className="werkzeug-text">Zustand</span>
            <select className="eingabe" value={p.zustand ?? ""} onChange={(e) => p.onZustand(e.target.value || null)}>
              <option value="">Grundfassung</option>
              {p.zustaende.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>
        )}
        {p.werkzeuge}
        {!nebeneinander && (
          <div className="segment" role="tablist" aria-label="Vorschau">
            {(["java", "bedrock"] as const).map((a) => (
              <button key={a} type="button" role="tab" aria-selected={sichtbar === a} className={sichtbar === a ? "aktiv" : ""} onClick={() => setAnsicht(a)}>
                {a === "java" ? "Java" : "Bedrock"}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="buehne" ref={buehne}>
        {sichtbar !== "bedrock" && (
          <figure className="buehne-teil">
            <Inventar
              ansicht={java}
              katalog={p.katalog}
              skala={skala}
              markierung={markierung}
              {...(p.auswahl ? { auswahl: p.auswahl } : {})}
              {...(p.fokus !== undefined ? { fokus: p.fokus } : {})}
              {...(p.notizen ? { notizen: p.notizen } : {})}
              {...(p.fremde ? { fremde: p.fremde } : {})}
              {...(p.onSlot ? { onSlot: p.onSlot } : {})}
              {...(p.onTaste ? { onTaste: p.onTaste } : {})}
              {...(p.slotHuelle ? { slotHuelle: p.slotHuelle } : {})}
              beschriftung="Java-Inventar"
            />
            <figcaption>
              <span>Java · {FORM_TEXT(p.daten.form)} · {slotAnzahl(p.daten.form)} Slots</span>
              <span className="legende" aria-label="Legende">
                <span className="legende-punkt legende-liste" /> Listenbereich
                <span className="legende-punkt legende-blaettern" /> Blätter-Slot
              </span>
            </figcaption>
          </figure>
        )}
        {sichtbar !== "java" && (
          <figure className="buehne-teil">
            <BedrockFormular seiten={bedrock} katalog={p.katalog} />
            <figcaption>
              <span>
                Bedrock · {bedrock.length} {bedrock.length === 1 ? "Seite" : "Seiten"}
              </span>
            </figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}
